import type { HTMLAttributes, ReactNode, Ref } from 'react'
import styles from './PageMasthead.module.css'

export interface PageMastheadProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Small line above the title, e.g. the org name. */
  eyebrow?: ReactNode
  title: ReactNode
  /** One line under the title. */
  subtitle?: ReactNode
  /**
   * Set on the title's line, at the right: MastheadNav tabs or action
   * buttons. Stacks under the title on a phone.
   */
  aside?: ReactNode
  as?: 'h1' | 'h2'
  ref?: Ref<HTMLElement>
}

/**
 * The top of a public page: a display title with tabs or actions beside it,
 * over a hairline rule. Org and Carousel pages share it so every section opens
 * the same way. (PageHeader is the sticky bar for admin tools; this is not.)
 */
export function PageMasthead({
  eyebrow,
  title,
  subtitle,
  aside,
  as: Heading = 'h1',
  className,
  ref,
  ...props
}: PageMastheadProps) {
  return (
    <header ref={ref} className={[styles.masthead, className].filter(Boolean).join(' ')} {...props}>
      <div className={styles.titleBlock}>
        {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
        <Heading className={styles.title}>{title}</Heading>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {aside && <div className={styles.aside}>{aside}</div>}
    </header>
  )
}
